import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message envoyé avec succès !');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adresse',
      content: '7 place des martyrs de la résistance',
      subcontent: '34000 Montpellier',
      gradient: 'from-blue-400 to-indigo-500',
    },
    {
      icon: Phone,
      title: 'Téléphones',
      content: 'Tél : 09 83 82 07 54',
      content2: 'SMS : 07 82 37 36 95',
      subcontent: 'Du lundi au samedi',
      gradient: 'from-green-400 to-emerald-500',
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'alloreparations@gmail.com',
      subcontent: 'Réponse sous 24h',
      gradient: 'from-purple-400 to-violet-500',
    },
    {
      icon: Clock,
      title: 'Horaires',
      content: 'Lun - Sam : 10h - 20h',
      subcontent: 'Dimanche : Fermé',
      gradient: 'from-amber-400 to-orange-500',
    },
  ];

  return (
    <section
      id="contact"
      className="section-spacing relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background */}
      <div className="absolute inset-0 gradient-section-cyan" />
      
      {/* Decorative blobs */}
      <div className="absolute top-0 right-1/4 w-80 h-80 bg-gradient-to-br from-cyan-400/20 to-blue-400/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-br from-purple-400/15 to-pink-400/15 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-full mb-6">
            <MapPin size={16} className="text-cyan-500" />
            <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider" data-testid="contact-label">
              Contact
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6" data-testid="contact-title">
            Venez nous{' '}
            <span className="gradient-text">rencontrer</span>
          </h2>
          <p className="text-lg text-slate-600" data-testid="contact-description">
            Notre boutique est située en plein centre de Montpellier. 
            Passez nous voir ou contactez-nous directement.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Map & Info */}
          <div className="space-y-8">
            {/* Map */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-200/50" data-testid="contact-map">
              {/* Gradient border */}
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 rounded-[28px] opacity-30 blur-sm" />
              <div className="relative h-[300px] lg:h-[400px] rounded-3xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2888.8955827542247!2d3.8767199!3d43.6108333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b6af0f8c3c7c9f%3A0x0!2s7%20Place%20des%20Martyrs%20de%20la%20R%C3%A9sistance%2C%2034000%20Montpellier!5e0!3m2!1sfr!2sfr!4v1640000000000!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation AlloRéparations Montpellier"
                />
              </div>
            </div>

            {/* Contact Info Grid */}
            <div className="grid grid-cols-2 gap-4" data-testid="contact-info-grid">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="group relative"
                  data-testid={`contact-info-${index}`}
                >
                  {/* Card glow */}
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${info.gradient} rounded-[22px] opacity-0 group-hover:opacity-30 blur transition-opacity`} />
                  
                  <div className="relative glass-card-colored rounded-2xl p-5 hover-lift h-full">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${info.gradient} flex items-center justify-center mb-4 shadow-lg`}>
                      <info.icon className="text-white" size={22} />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                    <p className="text-slate-700 text-sm font-medium">{info.content}</p>
                    {info.content2 && (
                      <p className="text-slate-700 text-sm font-medium">{info.content2}</p>
                    )}
                    <p className="text-slate-500 text-xs">{info.subcontent}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <div className="relative" data-testid="contact-form-card">
            {/* Form glow */}
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 rounded-[32px] opacity-20 blur-xl" />
            
            <div className="relative glass-card-colored rounded-3xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Send className="text-white" size={18} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Envoyez-nous un message
                </h3>
              </div>
              <p className="text-slate-600 mb-8">
                Nous vous répondrons dans les plus brefs délais.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 bg-white/80 border-2 border-slate-200/50 rounded-xl text-slate-900 placeholder-slate-400 focus:border-[#0052FF] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                    placeholder="Votre nom"
                    data-testid="input-name"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 bg-white/80 border-2 border-slate-200/50 rounded-xl text-slate-900 placeholder-slate-400 focus:border-[#0052FF] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      placeholder="votre@email.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-white/80 border-2 border-slate-200/50 rounded-xl text-slate-900 placeholder-slate-400 focus:border-[#0052FF] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      placeholder="06 XX XX XX XX"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-5 py-4 bg-white/80 border-2 border-slate-200/50 rounded-xl text-slate-900 placeholder-slate-400 focus:border-[#0052FF] focus:ring-4 focus:ring-blue-100 transition-all outline-none resize-none"
                    placeholder="Décrivez votre problème ou votre demande..."
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:-translate-y-1 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  data-testid="submit-contact"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send size={18} />
                      Envoyer le message
                    </span>
                  )}
                </button>

                {/* Trust indicators */}
                <div className="flex items-center justify-center gap-6 pt-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <CheckCircle size={14} className="text-green-500" />
                    Réponse sous 24h
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle size={14} className="text-green-500" />
                    Sans engagement
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
